/**
 * FunnelExperience
 *
 * Orchestrator component that evaluates the funnel step for a given page type
 * and renders the correct UI component (modal, banner, or popup).
 *
 * - onAccept (CTA button) = call accept API + add-to-cart, then dismiss
 * - onDismiss (X button) = temporary hide, reappears on next page visit
 * - onDecline ("No thanks") = call decline API, suppress for the rest of the session
 */

import { useCallback, useEffect, useState } from 'react'
import { useFunnelExperience } from './use-funnel-experience'
import { useFunnelRuntimeContext } from './use-funnel-runtime-context'
import type { PageContext } from './funnel-runtime.types'
import { FunnelModal } from './components/FunnelModal'
import { FunnelBanner } from './components/FunnelBanner'
import { FunnelPopup } from './components/FunnelPopup'
import { PopupTrigger, type PopupTriggerMode } from './components/PopupTrigger'
import { useCartStore } from '../../store/store/cart-store'

interface FunnelExperienceProps {
  pageType: PageContext['pageType']
  context: Omit<PageContext, 'pageType'>
}

export function FunnelExperience({ pageType, context }: FunnelExperienceProps) {
  const { loading, experience, offer, experienceType, dismiss, decline } =
    useFunnelExperience(pageType, context)
  const { acceptOffer, declineOffer } = useFunnelRuntimeContext()
  const [accepting, setAccepting] = useState(false)
  const [popupVisible, setPopupVisible] = useState(false)

  const handleAccept = useCallback(async () => {
    if (!experience || !offer) return
    setAccepting(true)
    try {
      await acceptOffer({
        stepId: experience.id,
        productId: offer.productId,
        variantId: offer.variantId,
        quantity: 1,
        action: 'add_to_cart',
      })

      useCartStore.getState().addItem({
        productId: offer.productId,
        name: offer.headline || offer.name,
        price: offer.pricingValue,
        imageUrl: offer.imageUrl,
        variantId: offer.variantId || undefined,
      })
    } catch (err) {
      console.error('[FunnelExperience] accept failed:', err)
    } finally {
      setAccepting(false)
      dismiss()
    }
  }, [experience, offer, acceptOffer, dismiss])

  const handleDecline = useCallback(async () => {
    if (!experience) {
      decline()
      return
    }
    try {
      await declineOffer(experience.id)
    } catch (err) {
      console.error('[FunnelExperience] decline failed:', err)
    }
    decline()
  }, [experience, declineOffer, decline])

  const handleDismiss = useCallback(() => {
    dismiss()
    setPopupVisible(false)
  }, [dismiss])

  const popupTriggerModeRaw = experience?.pageContent?.popupTriggerMode
  const popupTriggerMode: PopupTriggerMode =
    popupTriggerModeRaw === 'timer' ||
    popupTriggerModeRaw === 'scroll' ||
    popupTriggerModeRaw === 'exit-intent'
      ? popupTriggerModeRaw
      : 'any'

  const popupTimerMs = Number.parseInt(experience?.pageContent?.popupTimerMs ?? '8000', 10)
  const popupScrollPercent = Number.parseInt(experience?.pageContent?.popupScrollPercent ?? '50', 10)

  useEffect(() => {
    setPopupVisible(false)
  }, [experience?.id, experienceType])

  if (loading || !experience || !offer) {
    return null
  }

  switch (experienceType) {
    case 'modal':
      return (
        <FunnelModal
          offer={offer}
          onAccept={handleAccept}
          onDecline={handleDecline}
          onDismiss={handleDismiss}
          accepting={accepting}
        />
      )
    case 'banner':
      return (
        <FunnelBanner
          offer={offer}
          onAccept={handleAccept}
          onDecline={handleDecline}
          onDismiss={handleDismiss}
          accepting={accepting}
        />
      )
    case 'popup':
      return (
        <>
          {!popupVisible && (
            <PopupTrigger
              mode={popupTriggerMode}
              timerMs={Number.isFinite(popupTimerMs) ? popupTimerMs : 8000}
              scrollPercent={Number.isFinite(popupScrollPercent) ? popupScrollPercent : 50}
              onTrigger={() => setPopupVisible(true)}
            />
          )}
          {popupVisible && (
            <FunnelPopup
              offer={offer}
              onAccept={handleAccept}
              onDecline={handleDecline}
              onDismiss={handleDismiss}
              accepting={accepting}
            />
          )}
        </>
      )
    default:
      return null
  }
}
